import { useState, useEffect, useCallback } from 'react';
import { getDocumentsService } from '../services/billing';

/**
 * SHER-474: server-driven pagination for the Hospital billing browse list.
 *
 * Fetches ONE page of documents (/documents is already a slim projection server-side)
 * plus the full server `total`, so BillsTable can page through thousands of bills
 * without bulk-downloading them all. Mirrors useInsuranceBills. Patient-name search is
 * NOT a filter here — that's the patient-picker flow.
 */
interface UseBillsFilters {
    statuses?: number[];
    types?: number[];
    insuranceCodes?: string[];
    dateFrom?: string;
    dateTo?: string;
}

interface UseBillsProps {
    investigationUuid: string;
    page: number;
    rowsPerPage: number;
    filters?: UseBillsFilters;
    enabled?: boolean;
}

interface UseBillsResult {
    bills: any[];
    total: number;
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

export function useBills({
    investigationUuid,
    page,
    rowsPerPage,
    filters,
    enabled = true
}: UseBillsProps): UseBillsResult {
    const [bills, setBills] = useState<any[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchBills = useCallback(async () => {
        if (!enabled || !investigationUuid) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const response = await getDocumentsService(investigationUuid, {
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                slim: true,
                statuses: filters?.statuses,
                types: filters?.types,
                insuranceCodes: filters?.insuranceCodes,
                dateFrom: filters?.dateFrom,
                dateTo: filters?.dateTo,
            });
            if (response.status === 200) {
                setBills(response.bills || []);
                setTotal(typeof response.total === 'number' ? response.total : (response.bills?.length || 0));
            } else {
                setError('Failed to fetch bills');
                setBills([]);
                setTotal(0);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch bills');
            setBills([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investigationUuid, page, rowsPerPage, JSON.stringify(filters), enabled]);

    useEffect(() => {
        fetchBills();
    }, [fetchBills]);

    return { bills, total, loading, error, refetch: fetchBills };
}
